import React from 'react';
import './Body.scss'
import SwaggerUI from "swagger-ui-react";
import "swagger-ui-react/swagger-ui.css";
import APIEndpoint from '../../../images/API-Endpoints.svg';
import { BackToTopButton } from "../../../components/BackToTopButton/BackToTopButton";
import ChangeLog from "../../../data/documentation/ChangeLog";
import { ChangeLogBox } from "./ChangeLogBox/ChangeLogBox";
import { Tabs, TabPanel, ArrowIcon } from "@cmsgov/design-system";
import InpatientDataDictionary from "../../../data/documentation/InpatientDataDictionary";
import OutpatientDataDictionary from "../../../data/documentation/OutpatientDataDictionary";

const EXPORT_RESPONSE_BODY_MESSAGE = "Response body hidden for file downloads.";

interface ChangeLogEntry {
    summary: string,
    date: string,
    releaseDetails: string[]
}

interface DataDictionaryEntry {
    fieldName: string,
    dataType: string,
    definition: string[]
}

interface Props {
    type: string
}

type SwaggerVersion = "v1" | "v2"

interface State {
    changeLog: ChangeLogEntry[],
    changeLogState: boolean[],
    dataDictionaryType: string,
    docsBaseUrl: string,
    apiBaseUrl: string,
    swaggerVersion: SwaggerVersion,
    swaggerSpec?: any,
    dataDictionary: DataDictionaryEntry[]
    logExpanded: boolean
}

export class Body extends React.Component<Props, State> {
    constructor(Props: Props | Readonly<Props>) {
        super(Props);

        let defaultDataDictionaryType = "inpatient"
        // Local development can load docs from dev. Deployed sites use their own origin.
        const docsBaseUrl = process.env.REACT_APP_PDS_DOCS_BASE_URL || `${window.location.origin}`;

        this.state = {
            changeLog: ChangeLog,
            changeLogState: Array(ChangeLog.length).fill(false),
            dataDictionaryType: defaultDataDictionaryType,
            docsBaseUrl,
            // Swagger uses the docs URL unless an API base URL env var is configured.
            apiBaseUrl: process.env.REACT_APP_PDS_API_BASE_URL || docsBaseUrl,
            swaggerVersion: "v1",
            swaggerSpec: undefined,
            dataDictionary: InpatientDataDictionary,
            logExpanded: false
        }
    }

    componentDidMount() {
        this.loadSwaggerSpec();
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.type !== this.props.type) {
            this.loadSwaggerSpec();
        }
    }

    getSwaggerSpecUrl = () => `${this.state.docsBaseUrl}/swagger/v1/swagger.json`

    fetchSwaggerSpec = async (url: string) => {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Unable to load Swagger spec from ${url}`);
        }

        return response.json();
    }

    addSwaggerServer = (swaggerSpec: any) => ({
        ...swaggerSpec,
        servers: [{ url: this.state.apiBaseUrl }]
    })

    hideExportResponseBody = (response: any) => {
        const requestUrl = response?.url || response?.request?.url || "";

        if (!requestUrl.includes("/export")) {
            return response;
        }

        return {
            ...response,
            body: EXPORT_RESPONSE_BODY_MESSAGE,
            data: EXPORT_RESPONSE_BODY_MESSAGE,
            text: EXPORT_RESPONSE_BODY_MESSAGE
        };
    }

    isV2SwaggerPath = (path: string) => path.startsWith("/fiss/v2/")

    buildSwaggerSpecWithPaths = (swaggerSpec: any, paths: any, titleSuffix?: string) => {
        const swaggerTitle = swaggerSpec.info?.title || "ProviderDataService";

        return {
            ...swaggerSpec,
            info: {
                ...swaggerSpec.info,
                title: titleSuffix && !swaggerTitle.endsWith(` ${titleSuffix}`) ? `${swaggerTitle} ${titleSuffix}` : swaggerTitle
            },
            paths
        };
    }

    buildV1SwaggerSpec = (swaggerSpec: any) => {
        const v1Paths = Object.keys(swaggerSpec.paths || {})
            .filter((path) => !this.isV2SwaggerPath(path))
            .reduce((paths: any, path) => {
                paths[path] = swaggerSpec.paths[path];
                return paths;
            }, {});

        return this.buildSwaggerSpecWithPaths(swaggerSpec, v1Paths);
    }

    buildV2SwaggerSpec = (swaggerSpec: any) => {
        const v2Paths = Object.keys(swaggerSpec.paths || {})
            .filter(this.isV2SwaggerPath)
            .reduce((paths: any, path) => {
                paths[path] = swaggerSpec.paths[path];
                return paths;
            }, {});

        return this.buildSwaggerSpecWithPaths(swaggerSpec, v2Paths, "V2");
    }

    loadSwaggerSpec = async (swaggerVersion: SwaggerVersion = this.state.swaggerVersion) => {
        if (this.props.type !== "swagger" || typeof fetch === "undefined") {
            return;
        }

        try {
            let swaggerSpec = await this.fetchSwaggerSpec(this.getSwaggerSpecUrl());

            swaggerSpec = swaggerVersion === "v2"
                ? this.buildV2SwaggerSpec(swaggerSpec)
                : this.buildV1SwaggerSpec(swaggerSpec);
            this.setState({ swaggerSpec: this.addSwaggerServer(swaggerSpec), swaggerVersion });
        } catch {
            this.setState({ swaggerSpec: undefined });
        }
    }

    selectSwaggerVersion = (swaggerVersion: SwaggerVersion) => {
        if (swaggerVersion === this.state.swaggerVersion) {
            return;
        }

        this.setState({ swaggerSpec: undefined }, () => this.loadSwaggerSpec(swaggerVersion));
    }

    toggleChangeLogBox = (entryNumber: number, nextFocusElement: string) => {
        let newChangeLogState = this.state.changeLogState;
        newChangeLogState.forEach((entry, index) => {
            if (Number(index) === Number(entryNumber)) {
                newChangeLogState[index] = !entry;
            } else {
                newChangeLogState[index] = false;
            }
        })

        this.setState({ changeLogState: newChangeLogState })

        document.getElementById(nextFocusElement)?.focus();
    }

    toggleLogExpanded = () => {
        this.setState({ logExpanded: !this.state.logExpanded });
    }

    toggleDataDictionary = (dataType: string) => {
        let newDataDictionary;

        if (dataType === 'outpatient') {
            newDataDictionary = OutpatientDataDictionary
        } else {
            newDataDictionary = InpatientDataDictionary
        }

        this.setState({ dataDictionary: newDataDictionary, dataDictionaryType: dataType })
    }

    render(): JSX.Element {
        let DATA_DICTIONARY = this.state.dataDictionary.map((element) => (
            <div>
                <p>
                    <b tabIndex={0} role='heading' aria-level={1}>{element.fieldName}</b>
                    <div><b tabIndex={0} role='heading' aria-level={2}>Data Type: </b><span tabIndex={0}>{element.dataType}</span></div>
                    <ul role='list'>
                        {element.definition.map((entry) => (
                            <li role='listitem' aria-level={3} tabIndex={0} key={entry}>{entry}</li>
                        ))}
                    </ul>
                </p>

            </div>
        ));
        const LOG_EXPANDED = !this.state.logExpanded ? this.state.changeLog.filter((value, index) => index < 4) : this.state.changeLog;

        const CHANGE_LOG = LOG_EXPANDED.map((element, index) => (
            <div>
                <ChangeLogBox
                    summary={element.summary}
                    date={element.date}
                    releaseDetails={element.releaseDetails}
                    id={`${index}`}
                    onClick={this.toggleChangeLogBox}
                    isOpen={this.state.changeLogState[index]}
                />
                {
                    (index < this.state.changeLog.length - 1) ? (
                        <div className={"change-log-section-hr"} />
                    ) : null
                }

            </div>
        ));
        const isSwaggerPage = this.props.type === "swagger";

        return (
            <div role="main" className="doc-body ds-u-padding-bottom--6">
                <section className={`ds-l-container ds-base ${isSwaggerPage ? "developer-docs__swagger-container" : ""}`} role="application">
                    <div className="ds-l-row">
                    <article  id="main-content" className={isSwaggerPage ? "ds-l-col--12" : "ds-l-col--12 ds-l-sm-col--9"}>
                            {this.props.type === "dictionary" && (
                            <div>
                                <div className={"body-section-hr"}>
                                    <div className={`text`} tabIndex={0}>
                                        <Tabs onChange={(selectedId, prevSelectedId) => { this.toggleDataDictionary(selectedId) }}>
                                            <TabPanel
                                                key="inpatient"
                                                id="inpatient"
                                                tab="Inpatient"
                                                tabHref='#/Data_dictionary'
                                            >
                                                <div
                                                    className={"data-dictionary-entries ds-l-col--12 ds-l-sm-col--12"}>
                                                    {DATA_DICTIONARY}
                                                </div>
                                            </TabPanel>
                                            <TabPanel
                                                key="outpatient"
                                                id="outpatient"
                                                tab="Outpatient"
                                                tabHref='#/Data_dictionary'
                                            >
                                                <div
                                                    className={"data-dictionary-entries ds-l-col--12 ds-l-sm-col--12 ds-u-overflow--scroll"}>
                                                    {DATA_DICTIONARY}
                                                </div>
                                            </TabPanel>

                                        </Tabs>
                                    </div>

                                </div>
                            </div>)}
                            {this.props.type === "docs" && (<div>
                                <div className={"body-section-hr"}>
                                    <div className={`text`} tabIndex={0}>
                                        <p>Refer to our <a
                                            href="#/Swagger_documentation">Swagger
                                            documentation</a> to learn how to consume our REST API. <br />
                                            Human-readable documentation for using the Export endpoints is also <a
                                                href={`${this.state.docsBaseUrl}/UserGuideHistoricalPSFData.pdf`}>available
                                                here as PDF.</a>
                                        </p>
                                        <img src={APIEndpoint} alt={"API Endpoints"} />
                                    </div>
                                </div>
                            </div>)}
                            {this.props.type === "swagger" && (<div>
                                <div className={"body-section-hr"}>
                                    <div className={`text developer-docs__swagger`} tabIndex={0}>
                                        <p>
                                            <a href="#/Developer_documentation">Back to Developer Documentation</a>
                                        </p>
                                        <div className="developer-docs__swagger-version-controls">
                                            <button
                                                type="button"
                                                className={`ds-c-button developer-docs__swagger-version-button ${this.state.swaggerVersion === "v1" ? "ds-c-button--primary" : ""}`}
                                                onClick={() => this.selectSwaggerVersion("v1")}
                                            >
                                                V1 endpoints
                                            </button>
                                            <button
                                                type="button"
                                                className={`ds-c-button developer-docs__swagger-version-button ${this.state.swaggerVersion === "v2" ? "ds-c-button--primary" : ""}`}
                                                onClick={() => this.selectSwaggerVersion("v2")}
                                            >
                                                V2 endpoints
                                            </button>
                                        </div>
                                        {this.state.swaggerSpec && (
                                            <SwaggerUI
                                                spec={this.state.swaggerSpec}
                                                docExpansion="list"
                                                defaultModelsExpandDepth={-1}
                                                tryItOutEnabled={true}
                                                responseInterceptor={this.hideExportResponseBody}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>)}
                            {this.props.type === "log" && (<div>
                                <div className={`doc-change-log ds-l-col--10 ds-l-sm-col--12`} tabIndex={0}>
                                    {CHANGE_LOG}
                                </div>

                                <button className="ds-c-button ds-c-button--transparent show-more-less" onClick={this.toggleLogExpanded}>
                                    {!this.state.logExpanded ? "Show More " : "Show Less "}
                                    {!this.state.logExpanded ? <ArrowIcon direction='down' /> : <ArrowIcon direction='up' />}
                                </button>
                            </div>)}
                        </article>
                    </div>
                </section>
                <BackToTopButton />
            </div>
        );
    }
}
